import { ReactNode, useMemo } from 'react';
import {
  CheckboxRecaptchaConfig,
  CheckboxRecaptchaResult,
  useCheckboxRecaptcha,
} from './useCheckboxRecaptcha';
import { ScoreRecaptchaConfig, ScoreRecaptchaResult, useScoreRecaptcha } from './useScoreRecaptcha';
import { useAppSelector } from '../stores/redux.store';
import { captureErrorMessage } from '../utilities/analytics/error-reporting';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';

export type RecaptchaType = 'score' | 'checkbox';
export type ChallengeKind = 'recaptcha';

export type RecaptchaProps =
  | { type: 'score'; config: ScoreRecaptchaConfig }
  | { type: 'checkbox'; config: CheckboxRecaptchaConfig };

export type ChallengeResponse = {
  challengeKind: ChallengeKind;
  token: string;
  type: RecaptchaType;
};

export type UseRecaptchaResult = {
  fetchToken: () => Promise<ChallengeResponse | null>;
  isLoaded: boolean;
  enabled: boolean;
  errorMessage?: string;
  // checkbox only
  component?: ReactNode;
};

const noOpScoreConfig: ScoreRecaptchaConfig = { sitekey: '', pageId: '', enabled: false };
const noOpCheckboxConfig: CheckboxRecaptchaConfig = {
  sitekey: '',
  pageId: '',
  enabled: false,
  action: '',
};

export type UseRecaptchaProps = {
  pageId: string;
  type: RecaptchaType;
  enabledProp?: boolean;
  action: string;
};
export const useRecaptcha = (props: UseRecaptchaProps): UseRecaptchaResult => {
  const { pageId, type: typeFromProps, enabledProp = true, action } = props;
  const type = checkFeatureGate(AidFrontendFeatureGate.HOT_113002_FORCE_CHECKBOX)
    ? 'checkbox'
    : typeFromProps;
  const { config } = useCreateRecaptchaProps(pageId, type, enabledProp, action);

  const isScore = type === 'score';
  const isCheckbox = type === 'checkbox';
  const scoreRecaptcha = useScoreRecaptcha(isScore ? config : noOpScoreConfig);

  const checkboxRecaptcha = useCheckboxRecaptcha(
    isCheckbox ? (config as CheckboxRecaptchaConfig) : noOpCheckboxConfig
  );

  return useMemo(
    () => (isScore ? handleScore(scoreRecaptcha, action) : handleCheckbox(checkboxRecaptcha)),
    [isScore, scoreRecaptcha, checkboxRecaptcha, action]
  );
};

export function useCreateRecaptchaProps(
  pageId: string,
  type: RecaptchaType,
  enabledProp: boolean,
  action?: string
): RecaptchaProps {
  const captchaEnabledByConfig = useAppSelector(state => state.appConfig.recaptchaEnable);
  const scoreSitekey = useAppSelector(state => state.appConfig.recaptchaEnterpriseScoreKeySite);
  const checkboxSitekey = useAppSelector(
    state => state.appConfig.recaptchaEnterpriseCheckboxKeySite
  );

  const enabled = captchaEnabledByConfig && enabledProp;

  const commonConfig = {
    pageId,
    enabled,
  };

  const scoreConfig: ScoreRecaptchaConfig = {
    ...commonConfig,
    sitekey: scoreSitekey,
    displayBadge: false,
  };

  const checkboxConfig: CheckboxRecaptchaConfig = {
    ...commonConfig,
    sitekey: checkboxSitekey,
    action: action ?? 'unknown',
  };

  return type === 'score' ? { type, config: scoreConfig } : { type, config: checkboxConfig };
}

const handleCheckbox = (checkboxRecaptcha: CheckboxRecaptchaResult): UseRecaptchaResult => {
  const wrapToken = () => {
    if (checkFeatureGate(AidFrontendFeatureGate.SHOULD_FAIL_OPEN_RECAPTCHA)) {
      return Promise.resolve({
        token: checkboxRecaptcha.token || '',
        type: 'checkbox' as RecaptchaType,
        challengeKind: 'recaptcha' as ChallengeKind,
      });
    } else {
      return Promise.resolve(
        checkboxRecaptcha.token
          ? {
              token: checkboxRecaptcha.token,
              type: 'checkbox' as RecaptchaType,
              challengeKind: 'recaptcha' as ChallengeKind,
            }
          : null
      );
    }
  };

  const isLoaded = checkboxRecaptcha.isLoaded;
  const enabled = checkboxRecaptcha?.enabled;
  const errorMessage = checkboxRecaptcha?.error?.message ?? undefined;
  const component = checkboxRecaptcha.component;

  const onFetchToken = async () => {
    const response = await wrapToken();

    // TODO once the flag is cleaned up, the type of response will always be truthy so we can change the check to response.token === ''
    if ((!response || !response.token) && enabled) {
      if (checkFeatureGate(AidFrontendFeatureGate.SHOULD_FAIL_OPEN_RECAPTCHA)) {
        captureErrorMessage(errorMessage ?? 'no token returned from checkbox recaptcha');
      } else {
        throw new Error(errorMessage ?? 'no token returned from recaptcha');
      }
    }

    return response;
  };

  return {
    fetchToken: onFetchToken,
    isLoaded: isLoaded || false,
    enabled: enabled || false,
    errorMessage,
    component,
  };
};

const handleScore = (scoreRecaptcha: ScoreRecaptchaResult, action: string): UseRecaptchaResult => {
  const fetchAndWrapToken = (action: string) =>
    scoreRecaptcha.fetchRecaptchaToken(action).then(token => {
      if (checkFeatureGate(AidFrontendFeatureGate.SHOULD_FAIL_OPEN_RECAPTCHA)) {
        return {
          token: token || '',
          type: 'score' as RecaptchaType,
          challengeKind: 'recaptcha' as ChallengeKind,
        };
      } else {
        return token
          ? {
              token,
              type: 'score' as RecaptchaType,
              challengeKind: 'recaptcha' as ChallengeKind,
            }
          : null;
      }
    });
  const isLoaded = scoreRecaptcha.isLoaded;
  const enabled = scoreRecaptcha?.enabled;
  const errorMessage = scoreRecaptcha?.loadRecaptchaErrorReason ?? undefined;

  const onFetchToken = async () => {
    const response = await fetchAndWrapToken(action);

    // TODO once the flag is cleaned up, the type of response will always be truthy so we can change the check to response.token === ''
    if ((!response || !response.token) && enabled) {
      if (checkFeatureGate(AidFrontendFeatureGate.SHOULD_FAIL_OPEN_RECAPTCHA)) {
        captureErrorMessage(errorMessage ?? 'no token returned from score recaptcha');
      } else {
        throw new Error(errorMessage ?? 'no token returned from recaptcha');
      }
    }
    return response;
  };

  return {
    fetchToken: onFetchToken,
    isLoaded: isLoaded || false,
    enabled: enabled || false,
    errorMessage,
  };
};
